import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, MapPin, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./Input";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function SettingsTab() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", phone: "", email: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [health, setHealth] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [deleteAdminTarget, setDeleteAdminTarget] = useState(null);
  const [passwordAdminTarget, setPasswordAdminTarget] = useState(null);
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", name: "", phone: "", email: "" });

  // Daily export
  const [exportCfg, setExportCfg] = useState({ sheet_id: "", tab_name: "donations", export_hour: 23, export_minute: 30, enabled: false });
  const [exportMeta, setExportMeta] = useState({ last_export_at: null, last_export_count: null, last_export_date: null });
  const [runningExport, setRunningExport] = useState(false);
  const [officeLocation, setOfficeLocation] = useState(null);
  const [officeRadius, setOfficeRadius] = useState(200);
  const [savingOffice, setSavingOffice] = useState(false);

  // Salary deduction config
  const emptyRule = { amount: 0, unit: "flat" };
  const [dedCfg, setDedCfg] = useState({ absent: { ...emptyRule }, half_day: { ...emptyRule }, late: { ...emptyRule } });
  const [savingDed, setSavingDed] = useState(false);

  const [constants, setConstants] = useState({ sources: [], organizations: [] });
  const [newSource, setNewSource] = useState("");
  const [newOrg, setNewOrg] = useState("");
  const [savingConstants, setSavingConstants] = useState(false);

  const loadConstants = () => api.get("/admin/constants").then(r => setConstants({ sources: r.data.sources || [], organizations: r.data.organizations || [] }));

  const loadAdmins = () => api.get("/admin/admins").then(r => setAdmins(r.data));
  const loadOfficeLocation = () => api.get("/admin/office-location").then(r => {
    const d = r.data || {};
    setOfficeLocation(d.lat != null && d.lng != null ? d : null);
    setOfficeRadius(d.radius_meters || 200);
  }).catch(() => {
    setOfficeLocation(null);
    setOfficeRadius(200);
  });
  const loadDedCfg = () => api.get("/admin/deduction-config").then(r => setDedCfg(r.data || dedCfg));
  const loadExportCfg = () => api.get("/admin/export-sheet/config").then(r => {
    const d = r.data || {};
    setExportCfg({
      sheet_id: d.sheet_id || "",
      tab_name: d.tab_name || "donations",
      export_hour: d.export_hour ?? 23,
      export_minute: d.export_minute ?? 30,
      enabled: !!d.enabled,
    });
    setExportMeta({
      last_export_at: d.last_export_at || null,
      last_export_count: d.last_export_count ?? null,
      last_export_date: d.last_export_date || null,
    });
  });
  // Load settings when the signed-in administrator changes.
  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", email: user.email || "" });
    loadAdmins();
    loadConstants();
    loadExportCfg();
    loadOfficeLocation();
    loadDedCfg();
    api.get("/admin/health").then((r) => setHealth(r.data)).catch(() => setHealth({ status: "unavailable" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveExportCfg = async () => {
    try {
      await api.put("/admin/export-sheet/config", {
        sheet_id: exportCfg.sheet_id.trim(),
        tab_name: (exportCfg.tab_name || "donations").trim(),
        export_hour: parseInt(exportCfg.export_hour || 0),
        export_minute: parseInt(exportCfg.export_minute || 0),
        enabled: !!exportCfg.enabled,
      });
      toast.success("Export config saved");
      loadExportCfg();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };
  const runExportNow = async () => {
    setRunningExport(true);
    try {
      const r = await api.post("/admin/export-sheet/run");
      if (r.data.success) toast.success(`Exported ${r.data.exported || 0} donation(s) for ${r.data.date}`);
      else toast.error(r.data.error || "Export failed");
      loadExportCfg();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setRunningExport(false); }
  };

  const getCurrentLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location is not supported in this browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => reject(new Error("Please allow location access to set office location")),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });

  const normalizedOfficeRadius = () => {
    const radius = parseFloat(officeRadius || 0);
    if (!Number.isFinite(radius) || radius < 5 || radius > 5000) {
      toast.error("Radius must be between 5 and 5000 meters");
      return null;
    }
    return radius;
  };

  const setOfficeFromHere = async () => {
    const radius = normalizedOfficeRadius();
    if (radius == null) return;
    setSavingOffice(true);
    try {
      const location = await getCurrentLocation();
      const r = await api.put("/admin/office-location", {
        location,
        radius_meters: radius,
      });
      setOfficeLocation(r.data || null);
      setOfficeRadius(r.data?.radius_meters || 200);
      toast.success("Office location saved");
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingOffice(false); }
  };

  const saveOfficeRadius = async () => {
    const radius = normalizedOfficeRadius();
    if (radius == null) return;
    if (!officeLocation) {
      toast.error("Click Set office location first");
      return;
    }
    setSavingOffice(true);
    try {
      const r = await api.put("/admin/office-location", {
        location: { lat: officeLocation.lat, lng: officeLocation.lng, address: officeLocation.address },
        radius_meters: radius,
      });
      setOfficeLocation(r.data || null);
      setOfficeRadius(r.data?.radius_meters || 200);
      toast.success(`Office radius saved to ${Math.round(r.data?.radius_meters || radius)}m`);
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingOffice(false); }
  };

  const updateDed = (key, field, value) => {
    setDedCfg({ ...dedCfg, [key]: { ...dedCfg[key], [field]: value } });
  };
  const saveDedCfg = async () => {
    setSavingDed(true);
    try {
      const body = {
        absent:   { amount: parseFloat(dedCfg.absent.amount || 0),   unit: dedCfg.absent.unit || "flat" },
        half_day: { amount: parseFloat(dedCfg.half_day.amount || 0), unit: dedCfg.half_day.unit || "flat" },
        late:     { amount: parseFloat(dedCfg.late.amount || 0),     unit: dedCfg.late.unit || "flat" },
      };
      await api.put("/admin/deduction-config", body);
      toast.success("Deduction rules saved");
      loadDedCfg();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingDed(false); }
  };

  const addSource = () => {
    const v = newSource.trim();
    if (!v || constants.sources.includes(v)) { setNewSource(""); return; }
    setConstants({ ...constants, sources: [...constants.sources, v] });
    setNewSource("");
  };
  const removeSource = (s) => setConstants({ ...constants, sources: constants.sources.filter(x => x !== s) });
  const addOrg = () => {
    const v = newOrg.trim();
    if (!v || constants.organizations.includes(v)) { setNewOrg(""); return; }
    setConstants({ ...constants, organizations: [...constants.organizations, v] });
    setNewOrg("");
  };
  const removeOrg = (o) => setConstants({ ...constants, organizations: constants.organizations.filter(x => x !== o) });

  const saveConstants = async () => {
    setSavingConstants(true);
    try {
      await api.put("/admin/constants", constants);
      toast.success("Dropdown options saved");
      await loadConstants();
      window.dispatchEvent(new Event("kaya:constants-updated"));
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingConstants(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", profile);
      toast.success("Profile updated");
      refresh();
      loadAdmins();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw.password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.patch("/auth/me", { current_password: pw.current_password, password: pw.password });
      toast.success("Password changed");
      setPw({ current_password: "", password: "", confirm: "" });
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingPw(false); }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/admins", newAdmin);
      toast.success(`Admin ${newAdmin.username} added`);
      setNewAdmin({ username: "", password: "", name: "", phone: "", email: "" });
      setCreating(false);
      loadAdmins();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const resetAdminPassword = async (a) => {
    const p = newAdminPassword;
    if (!p) return;
    if (p.length < 6) { toast.error("Password too short (min 6 characters)"); return; }
    try {
      await api.patch(`/admin/admins/${a.id}`, { password: p });
      toast.success("Password reset");
      setPasswordAdminTarget(null);
      setNewAdminPassword("");
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/admin/admins/${a.id}`, { active: !a.active });
      loadAdmins();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const deleteAdmin = async (a) => {
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Deleted");
      loadAdmins();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div>
        <div className="text-overline">Account</div>
        <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Settings</h2>
      </div>

      <div className="card-warm p-5" data-testid="operational-health">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><div className="text-overline">System</div><h3 className="text-lg font-medium text-[#2D312E]">Operational health</h3></div>
          <span className={`text-xs px-3 py-1 rounded-full border ${health?.status === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{health?.status || "checking"}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm">
          <HealthItem label="Database" value={health?.database?.status || "Checking…"} sub={health?.database?.response_ms != null ? `${health.database.response_ms} ms` : ""} />
          <HealthItem label="Sheet connection" value={health?.sheet_sync?.configured ? "Configured" : "Not configured"} sub={health?.sheet_sync?.enabled ? "Automatic sync enabled" : "Automatic sync disabled"} />
          <HealthItem label="Sheet worker" value={health?.sheet_sync?.running ? (health.sheet_sync.stage || "Running") : "Idle"} sub={health?.sheet_sync?.last_error || "No active error"} danger={!!health?.sheet_sync?.last_error} />
        </div>
      </div>

      {/* My profile */}
      <form onSubmit={saveProfile} className="card-warm p-6 space-y-4" data-testid="settings-profile">
        <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>My profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Full name" testid="settings-name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} />
          <div>
            <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Username (read-only)</label>
            <input value={user?.username || ""} readOnly
              className="w-full bg-[#F4F1EA] border border-[#EAE6DF] rounded-lg px-4 py-2.5 text-[#8A938D]" />
          </div>
          <Input label="Phone" testid="settings-phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} />
          <Input label="Email" testid="settings-email" value={profile.email} onChange={(v) => setProfile({ ...profile, email: v })} />
        </div>
        <button data-testid="save-profile" type="submit" disabled={savingProfile}
          className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
          {savingProfile ? "Saving…" : "Save profile"}
        </button>
      </form>

      {/* Change password */}
      <form onSubmit={savePassword} className="card-warm p-6 space-y-4" data-testid="settings-password">
        <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Change password</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Current password" testid="settings-current-password" type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} />
          <Input label="New password" testid="settings-new-password" type="password" value={pw.password} onChange={(v) => setPw({ ...pw, password: v })} />
          <Input label="Confirm new password" testid="settings-confirm-password" type="password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} />
        </div>
        <button data-testid="save-password" type="submit" disabled={savingPw}
          className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
          {savingPw ? "Saving…" : "Change password"}
        </button>
      </form>

      {/* Donation form dropdown options */}
      <div className="card-warm p-6 space-y-4" data-testid="settings-constants">
        <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Donation form dropdowns</h3>
        <p className="text-sm text-[#5C635E]">Manage the &ldquo;Source&rdquo; and &ldquo;Organization&rdquo; options that employees see when adding a donation.</p>

        <div>
          <div className="text-sm font-medium text-[#5C635E] mb-2">Sources ({constants.sources.length})</div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="constants-sources-list">
            {constants.sources.map((s) => (
              <span key={s} data-testid={`source-chip-${s}`} className="inline-flex items-center gap-2 px-3 py-1 bg-[#FDFBF7] border border-[#EAE6DF] rounded-full text-sm text-[#2D312E]">
                {s}
                <button type="button" onClick={() => removeSource(s)} className="text-[#C05746]" data-testid={`remove-source-${s}`}>×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="new-source" value={newSource} onChange={(e) => setNewSource(e.target.value)}
              placeholder="e.g. paytm, phonepe, cheque"
              className="flex-1 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2 text-sm" />
            <button data-testid="add-source" type="button" onClick={addSource}
              className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E]">Add</button>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-[#5C635E] mb-2">Organizations ({constants.organizations.length})</div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="constants-orgs-list">
            {constants.organizations.map((o) => (
              <span key={o} data-testid={`org-chip-${o}`} className="inline-flex items-center gap-2 px-3 py-1 bg-[#FDFBF7] border border-[#EAE6DF] rounded-full text-sm text-[#2D312E] uppercase">
                {o}
                <button type="button" onClick={() => removeOrg(o)} className="text-[#C05746]" data-testid={`remove-org-${o}`}>×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="new-org" value={newOrg} onChange={(e) => setNewOrg(e.target.value)}
              placeholder="e.g. new NGO code"
              className="flex-1 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2 text-sm" />
            <button data-testid="add-org" type="button" onClick={addOrg}
              className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E]">Add</button>
          </div>
        </div>

        <button data-testid="save-constants" onClick={saveConstants} disabled={savingConstants}
          className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
          {savingConstants ? "Saving…" : "Save dropdown options"}
        </button>
      </div>

      {/* Office location */}
      <div className="card-warm p-6 space-y-4" data-testid="settings-office-location">
        <div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Office location</h3>
          <p className="text-sm text-[#5C635E]">Set this while standing in office. Employees can clock in only when their current location is inside the allowed radius.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-xs text-[#5C635E] mb-1 block">Allowed radius (meters)</label>
            <input data-testid="office-radius" type="number" min="5" max="5000" value={officeRadius}
              onChange={(e) => setOfficeRadius(e.target.value)}
              className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
          </div>
          <button data-testid="set-office-location" type="button" onClick={setOfficeFromHere} disabled={savingOffice}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm flex items-center justify-center gap-2">
            {savingOffice ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            Set office location
          </button>
          <button data-testid="save-office-radius" type="button" onClick={saveOfficeRadius} disabled={savingOffice}
            className="border border-[#EAE6DF] hover:border-[#4A5D4E] disabled:opacity-60 rounded-xl px-6 py-2.5 text-sm text-[#2D312E]">
            Save radius
          </button>
        </div>
        {officeLocation ? (
          <div className="text-xs text-[#5C635E] border border-[#EAE6DF] rounded-lg px-3 py-2 bg-[#FDFBF7]" data-testid="office-location-current">
            Current office: {Number(officeLocation.lat).toFixed(6)}, {Number(officeLocation.lng).toFixed(6)} · Saved radius {Math.round(officeLocation.radius_meters || 200)}m
          </div>
        ) : (
          <div className="text-xs text-[#C05746]" data-testid="office-location-missing">Office location is not set. Employees cannot clock in until it is set.</div>
        )}
      </div>

      {/* Deduction config */}
      <div className="card-warm p-6 space-y-4" data-testid="settings-deductions">
        <div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Salary deductions</h3>
          <p className="text-sm text-[#5C635E]">Amount deducted for each attendance issue. Leave any row at ₹0 (or 0%) to skip that deduction. Percent = % of daily wage (basic salary divided by calendar days in month).</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Unit</th>
              </tr>
            </thead>
            <tbody>
              {[
                { key: "absent",   label: "Absent day" },
                { key: "half_day", label: "Half day" },
                { key: "late",     label: "Late mark" },
              ].map(({ key, label }) => (
                <tr key={key} className="border-t border-[#EAE6DF]" data-testid={`ded-row-${key}`}>
                  <td className="p-3 font-medium text-[#2D312E]">{label}</td>
                  <td className="p-3">
                    <input type="number" step="0.01" min="0"
                      data-testid={`ded-${key}-amount`}
                      value={dedCfg[key].amount}
                      onChange={(e) => updateDed(key, "amount", e.target.value)}
                      className="w-32 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5 text-sm" />
                  </td>
                  <td className="p-3">
                    <select data-testid={`ded-${key}-unit`}
                      value={dedCfg[key].unit}
                      onChange={(e) => updateDed(key, "unit", e.target.value)}
                      className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5 text-sm">
                      <option value="flat">₹ flat</option>
                      <option value="percent">% of daily wage</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <button data-testid="save-deductions" onClick={saveDedCfg} disabled={savingDed}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
            {savingDed ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Approved donations export to Google Sheet */}
      <div className="card-warm p-6 space-y-4" data-testid="settings-export">
        <div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Approved donations export</h3>
          <p className="text-sm text-[#5C635E]">Append approved donations that have not been exported yet. This uses the service-account credential configured under Google Sheet Sync. After a successful export, those donations are marked exported and skipped in future runs.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Google Sheet ID" testid="export-sheet-id" value={exportCfg.sheet_id}
            onChange={(v) => setExportCfg({ ...exportCfg, sheet_id: v })} />
          <Input label="Tab name" testid="export-tab-name" value={exportCfg.tab_name}
            onChange={(v) => setExportCfg({ ...exportCfg, tab_name: v })} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#5C635E] mb-1 block">Export hour (IST)</label>
              <input data-testid="export-hour" type="number" min="0" max="23" value={exportCfg.export_hour}
                onWheel={(e) => e.currentTarget.blur()}
                onChange={(e) => setExportCfg({ ...exportCfg, export_hour: e.target.value })}
                className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-[#5C635E] mb-1 block">Export minute</label>
              <input data-testid="export-minute" type="number" min="0" max="59" value={exportCfg.export_minute}
                onWheel={(e) => e.currentTarget.blur()}
                onChange={(e) => setExportCfg({ ...exportCfg, export_minute: e.target.value })}
                className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#2D312E]">
            <input data-testid="export-enabled" type="checkbox" checked={!!exportCfg.enabled}
              onChange={(e) => setExportCfg({ ...exportCfg, enabled: e.target.checked })} />
            Enable automatic export
          </label>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button data-testid="save-export-cfg" onClick={saveExportCfg}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-6 py-2.5 text-sm">Save</button>
          <button data-testid="run-export-now" onClick={runExportNow} disabled={runningExport}
            className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-xl px-6 py-2.5 text-sm text-[#2D312E] disabled:opacity-60">
            {runningExport ? "Running…" : "Run export now"}
          </button>
        </div>

        {exportMeta.last_export_at && (
          <div className="text-xs text-[#8A938D] border-t border-[#EAE6DF] pt-2" data-testid="export-last">
            Last run: {exportMeta.last_export_at.slice(0, 19).replace("T", " ")} · Exported {exportMeta.last_export_count} row(s)
          </div>
        )}
      </div>

      {/* Other admins */}
      <div className="card-warm p-6 space-y-4" data-testid="settings-admins">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Admins ({admins.length})</h3>
          <button data-testid="add-admin-button" onClick={() => setCreating(v => !v)}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-4 py-2 text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add admin
          </button>
        </div>

        {creating && (
          <form onSubmit={createAdmin} className="border border-[#EAE6DF] rounded-xl p-4 bg-[#FDFBF7] grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="new-admin-form">
            <Input label="Username*" testid="new-admin-username" value={newAdmin.username} onChange={(v) => setNewAdmin({ ...newAdmin, username: v })} />
            <Input label="Password*" testid="new-admin-password" type="password" value={newAdmin.password} onChange={(v) => setNewAdmin({ ...newAdmin, password: v })} />
            <Input label="Full name*" testid="new-admin-name" value={newAdmin.name} onChange={(v) => setNewAdmin({ ...newAdmin, name: v })} />
            <Input label="Phone" testid="new-admin-phone" value={newAdmin.phone} onChange={(v) => setNewAdmin({ ...newAdmin, phone: v })} />
            <Input label="Email" testid="new-admin-email" value={newAdmin.email} onChange={(v) => setNewAdmin({ ...newAdmin, email: v })} />
            <div className="md:col-span-2 flex gap-3">
              <button data-testid="save-new-admin" type="submit" className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-6 py-2 text-sm">Create admin</button>
              <button type="button" onClick={() => setCreating(false)} className="text-[#5C635E] text-sm">Cancel</button>
            </div>
          </form>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Username</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} data-testid={`admin-row-${a.id}`} className="border-t border-[#EAE6DF]">
                <td className="p-3 font-medium text-[#2D312E]">
                  {a.name}
                  {a.id === user?.id && <span className="ml-2 text-[10px] text-emerald-700">You</span>}
                </td>
                <td className="p-3 text-[#5C635E] font-mono text-xs">{a.username}</td>
                <td className="p-3 text-[#5C635E]">{a.email || "—"}</td>
                <td className="p-3">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${a.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-[#F4F1EA] text-[#8A938D] border border-[#EAE6DF]"}`}>
                    {a.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-3 text-right">
                  {a.id !== user?.id && (
                    <>
                      <button data-testid={`reset-admin-password-${a.id}`} onClick={() => { setPasswordAdminTarget(a); setNewAdminPassword(""); }} className="text-sm text-[#4A5D4E] hover:underline mr-3">
                        Reset password
                      </button>
                      <button data-testid={`toggle-admin-${a.id}`} onClick={() => toggleActive(a)} className="text-sm text-[#4A5D4E] hover:underline mr-3">
                        {a.active ? "Deactivate" : "Reactivate"}
                      </button>
                      <button data-testid={`delete-admin-${a.id}`} onClick={() => setDeleteAdminTarget(a)} className="text-sm text-[#C05746] hover:underline">
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={!!deleteAdminTarget}
        title="Delete admin?"
        message={`Delete admin ${deleteAdminTarget?.name}?`}
        confirmLabel="Delete"
        onConfirm={() => { const admin = deleteAdminTarget; setDeleteAdminTarget(null); if (admin) deleteAdmin(admin); }}
        onCancel={() => setDeleteAdminTarget(null)}
      />
      {passwordAdminTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="reset-admin-password-dialog">
          <div className="card-warm bg-white p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Reset password</h3>
              <button type="button" onClick={() => setPasswordAdminTarget(null)} className="text-[#8A938D]"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-sm text-[#5C635E]">Enter a new password for {passwordAdminTarget.name} ({passwordAdminTarget.username}).</div>
            <Input label="New password" type="password" value={newAdminPassword} onChange={(v) => setNewAdminPassword(v)} />
            <div className="flex gap-3">
              <button type="button" onClick={() => resetAdminPassword(passwordAdminTarget)}
                className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-5 py-2.5 text-sm">Save password</button>
              <button type="button" onClick={() => setPasswordAdminTarget(null)} className="text-[#5C635E] text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthItem({ label, value, sub, danger }) {
  return <div className="border border-[#EAE6DF] rounded-xl bg-[#FDFBF7] p-3"><div className="text-[10px] uppercase tracking-widest text-[#8A938D] font-bold">{label}</div><div className={`text-sm font-medium mt-1 ${danger ? "text-[#C05746]" : "text-[#2D312E]"}`}>{value}</div>{sub && <div className={`text-xs mt-1 break-words ${danger ? "text-[#C05746]" : "text-[#8A938D]"}`}>{sub}</div>}</div>;
}
